<script setup>
import { useStorage } from '@vueuse/core';
import { useAnnouncementStore } from '@/stores/announcement';

const announcementStore = useAnnouncementStore();
const announcement = computed(() => announcementStore.announcementHtml);

const ready = ref(false);
const dismissed = useStorage('announcement-bar-dismissed', false);
const atBottom = ref(false);

const show = computed(() => ready.value && !dismissed.value && !atBottom.value && !!announcement.value);

function dismiss() {
    dismissed.value = true;
}

onMounted(() => {
    function checkScroll() {
        const scrollBottom = window.innerHeight + window.scrollY;
        const docHeight = document.documentElement.scrollHeight;
        atBottom.value = scrollBottom >= docHeight - 10;
    }

    window.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();

    nextTick(() => {
        ready.value = true;
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', checkScroll);
    });
});
</script>

<template>
    <Transition name="announcement">
        <div
        v-if="show"
            class="block-announcement-bar pointer-events-auto bg-(--magenta) flex items-center justify-between pl-[18px] pr-6 py-4 md:px-4 md:py-3 md:gap-2.5 md:w-auto rounded-(--radius)"
        >
            <div class="font-hubspot-sans font-light text-[14px] text-white tracking-[0.28px] leading-normal [&_a]:underline [&_a]:decoration-dotted [&_a]:decoration-[8.5%] [&_a]:underline-offset-4 [&_a]:text-white [&_a]:hover:no-underline">
                <MessHtml :html="announcement" />
            </div>
            <button
                type="button"
                aria-label="Dismiss announcement"
                class="shrink-0 p-2 text-[#FCC3DC] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm cursor-pointer transition-colors"
                @click="dismiss"
            >
                <!-- Mobile X -->
                <svg class="w-3 h-3 md:hidden" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M0.5 11.5L6 6M6 6L11.5 0.5M6 6L0.5 0.5M6 6L11.5 11.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <!-- Desktop X -->
                <svg class="hidden md:display-block w-2 h-1.75" viewBox="0 0 8 7" fill="none" aria-hidden="true">
                    <path d="M1 6.5L4 3.5M4 3.5L7 0.5M4 3.5L1 0.5M4 3.5L7 6.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>
        </div>
    </Transition>
</template>

<style>
.announcement-enter-active,
.announcement-leave-active {
    transition: transform 0.4s ease, opacity 0.4s ease;
}

.announcement-enter-from,
.announcement-leave-to {
    opacity: 0;
    transform: translateX(-100%);
}

html.locked .block-announcement-bar {
    display: none;
}

@media (prefers-reduced-motion: reduce) {
    .announcement-enter-active,
    .announcement-leave-active {
        transition: none;
    }

    .announcement-enter-from,
    .announcement-leave-to {
        opacity: 1;
        transform: none;
    }
}
</style>
